import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import {ToastModule} from 'primeng/toast'
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TreeTableModule } from 'primeng/treetable';
import { TabViewModule } from 'primeng/tabview';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { AppComponent } from './app.component';
import { Globals } from './globals';
import { MessagesComponent } from './messages/messages.component';
import { MessagesService } from './messages/messages.service';
import { TicketComponent } from './ticket/ticket.component';
import { TicketService } from './ticket/ticket.service';
import { DataViewModule } from 'primeng/dataview';
import { ChartModule } from 'primeng/chart';
import { MasterdataComponent } from './masterdata/masterdata.component';
import { LoginComponent } from './login/login.component';
import { AuthGuard } from './common-services/auth.guard';
import { AuthenticationService } from './common-services/authentication.service';
import { AzureAppInsightsService } from './common-services/appinsights.azure.service';
import { MasterDataService } from './masterdata/masterdata.service';
import { CrudTableComponent } from './crud-table/crud-table.component';
import { CheckboxModule } from 'primeng/checkbox';
import { OrderListModule } from 'primeng/orderlist';
import { TooltipModule } from 'primeng/tooltip';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { LabComponent } from './lab/lab.component';
import { HeaderComponent } from './header/header.component';
import { LabGuard } from './common-services/lab.guard';
import { LabEmitterService } from './common-services/lab-emitter.service';
import { NgxFileHelpersModule } from 'ngx-file-helpers';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SpinnerModule } from 'primeng/spinner';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { MultiSelectModule } from 'primeng/multiselect';
import { ClientNamePipe } from './pipes/client-name.pipe';
import { ListboxModule } from 'primeng/listbox';
import { PanelModule } from 'primeng/panel';
import { CardModule } from 'primeng/card';
import { StandardPrimerComponent } from './standard-primer/standard-primer.component';
import { BarcodeTemplateComponent } from './barcode-template/barcode-template.component';
import { MdmProcessStepComponent } from './mdm-process-step/mdm-process-step.component';
import { MdmProcessStepService } from './mdm-process-step/mdm-process-step.service';
import { PickListModule } from 'primeng/picklist';
import {SliderModule} from 'primeng/slider';
import { DatePipe } from '@angular/common';
import { AgptestingNewviewComponent } from './agptesting-newview/agptesting-newview.component';
import { APGTestingService } from './agptesting-newview/apgtesting.service';
import { AppService } from './app.service';
import { ApgtestingUnboxingComponent } from './apgtesting-unboxing/apgtesting-unboxing.component';
import { SendersTemplateComponent } from './senders-template/senders-template.component';
import { UnboxingDirtyCheckGuard } from './apgtesting-unboxing/unboxing-dirty-check-guard';
import { AzureAuthGuard } from './common-services/auth.azure.guard';
import { AccessTokenGuard } from './common-services/access.token.guard';
import { AccessTokenInterceptor } from './common-services/access.token.interceptor';
import { KitsTemplateComponent } from './kits-template/kits-template.component';
import { MdmGuard } from './common-services/mdm.guard';
import { JwtInterceptor } from './common-services/jwt.interceptor';
import { NewViewDirtyCheckGuard } from './agptesting-newview/newview-dirty-check-guard';
import { BUGuard } from './common-services/bu.guard';
import { environment } from "../environments/environment";
import { RefreshAuthenticationService } from './common-services/access-token-refresh';
import { ApgAdministrationComponent } from './apg-administration/apg-administration.component';
import { APGAdministrationService } from './apg-administration/apg-administration.service';
import { ProblemCategoryTemplateComponent } from './mdm-problem-category-template/problem-category-template.component';
import { ProblemCategoryService } from './mdm-problem-category-template/problem-category-service';
import { ProblemComponent } from './mdm-nc-problem/problem.component';
import { ProblemService } from './mdm-nc-problem/problem-service';
import { MdmResultProcessComponent } from './mdm-result-process/mdm-result-process.component';
import { ResultProcessService } from './mdm-result-process/mdm-result-service';
import { ResultEngineConfigComponent } from './mdm-result-engine-config/result-engine-config.component';
import { ApgSampleResultViewComponent } from './apg-sample-result-view/apg-sample-result-view.component';
import { ExteralSenderLoginTemplateComponent } from './mdm-exteral-sender-login-template/exteral-sender-login-template.component';
import { ManufacturerConfigComponent } from './mdm-manufacturer-config/manufacturer-config.component';
import { HeaderService } from './header/header.service.';
import { PlateViewComponent } from './plate-view/plate-view.component';
import { TagsComponent } from './mdm-tags/tags.component';
import { PrintService } from './utils/print.service';
import { MdmTagSystemComponent } from './mdm-tag-system/mdm-tag-system.component';
import { NumberFormatPipe } from './pipes/number.pipe';
import { ArticLabPlanningComponent } from './artic-lab-planning/artic-lab-planning.component';
import { ArticLabPlanningService } from './artic-lab-planning/artic-lab-planning.service';
import { MdmWorkflowComponent } from './mdm-workflow/mdm-workflow.component';
import { ArticDeliveryPlanningComponent } from './artic-delivery-planning/artic-delivery-planning.component';
import { ArticDeliveryPlanningService } from './artic-delivery-planning/artic-delivery-planning.service';
import { PrintPlateLabelsComponent } from './print-plate-labels/print-plate-labels.component';
import { PrintPlateLabelService } from './print-plate-labels/print-plate-labels-service';
import { MidplexMergeComponent } from './midplex-merge/midplex-merge.component';
import { MidplexSamplesComponent } from './midplex-samples/midplex-samples.component';
import { MidplexSamplesService } from './midplex-samples/midplex-samples.service';
import { MdmSnpProfileComponent } from './mdm-snp-profile/mdm-snp-profile.component';
import { NGSLibraryQCService } from './ngs-library-qc/ngs-library-qc-service';
import { NgsFragmentAnalysisService } from './ngs-fragment-analysis/ngs-fragment-analysis.service';
import { NgsVolumeAdjustmentService } from './ngs-volume-adjustment/ngs-volume-adjustment.service';
import { NgsVolumeAdjustmentComponent } from './ngs-volume-adjustment/ngs-volume-adjustment.component';
import { NgsFragmentAnalysisComponent } from './ngs-fragment-analysis/ngs-fragment-analysis.component';
import { NGSLibraryQCComponent } from './ngs-library-qc/ngs-library-qc.component';
import { EntryQcComponent } from './entry-qc/entry-qc.component';
import {ChipsModule} from 'primeng/chips';
import {InputNumberModule} from 'primeng/inputnumber';
import { MidplexMergeService } from './midplex-merge/midplex-merge.service';
import { InvoicingItemComponent } from './invoicing-item/invoicing-item.component';
import {PaginatorModule} from 'primeng/paginator';
import { CustomerTrackComponent } from './customer-track/customer-track.component';
import { RunplanMainviewComponent } from './runplan-mainview/runplan-mainview.component';
import { RunPlanMainViewService } from './runplan-mainview/runplan-mainview.service';
import { MdmFlowcellComponent } from './mdm-flowcell/mdm-flowcell.component';
import { RunplanProcessComponent } from './runplan-process/runplan-process.component';
import { RunplanProcessService } from './runplan-process/runplan-process.service';
import { MdmAnalysisComponent } from './mdm-analysis/mdm-analysis.component';
import { MdmProcesstypeComponent } from './mdm-processtype/mdm-processtype.component';
import { MdmStationComponent } from './mdm-station/mdm-station.component';
import { MdmProcessComponent } from './mdm-process/mdm-process.component';
import { MdmInboundFileProcessingComponent } from './mdm-inbound-file-processing/mdm-inbound-file-processing.component';
import { MdmInboundFileProcessingService } from './mdm-inbound-file-processing/mdm-inbound-file-processing.service';
import { RunplanOnholdprojectComponent } from './runplan-onholdproject/runplan-onholdproject.component';
import { SidebarModule } from "primeng/sidebar";
import { RunplanSchedulerComponent } from './runplan-scheduler/runplan-scheduler.component';
import { RunplanSchedulerService } from './runplan-scheduler/runplan-scheduler.service';
import { MdmLibraryConfigurationComponent } from './mdm-library-configuration/mdm-library-configuration.component';
import { MdmLibraryConfigurationService } from './mdm-library-configuration/mdm-library-configuration-service';
import { MdmWorkfilesConfigComponent } from './mdm-workfiles-config/mdm-workfiles-config.component';
import { MdmWorkfilesConfigService } from './mdm-workfiles-config/mdm-workfiles-config.service';
import { RunplanTracklibrariesComponent } from './runplan-tracklibraries/runplan-tracklibraries.component';
import { RunplanTracklibrariesService } from './runplan-tracklibraries/runplan-tracklibraries.service';
import { MdmStepCategoryComponent } from './mdm-step-category/mdm-step-category.component';
import { LibprepWorkfilesComponent } from './ngs-libprep-workfiles/libprep-workfiles.component';
import { LibprepWorkfilesService } from './ngs-libprep-workfiles/libprep-workfiles.service';
import { NgsSampleArrivalComponent } from './ngs-sample-arrival/ngs-sample-arrival.component'
import { NgsSampleArrivalService } from './ngs-sample-arrival/ngs-sample-arrival.service';
import { ExtractionComponent } from './extraction/extraction.component';
import { ExtractionService } from './extraction/extraction.service';
import { PrintTemplateUtil } from './common-services/print-template-utils';
import { TreeModule } from 'primeng/tree';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { CalendarModule } from 'primeng/calendar';
import { MessageModule } from 'primeng/message';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ColorPickerModule } from 'primeng/colorpicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { GalleriaModule } from 'primeng/galleria';
import { KeyFilterModule } from 'primeng/keyfilter';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToggleButtonModule } from 'primeng/togglebutton';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LabComponent,
    TicketComponent,
    MessagesComponent,
    MasterdataComponent,
    LoginComponent,
    CrudTableComponent,
    ClientNamePipe,
    NumberFormatPipe,
    StandardPrimerComponent,
    BarcodeTemplateComponent,
    MdmProcessStepComponent,
    AgptestingNewviewComponent,
    ApgtestingUnboxingComponent,
    SendersTemplateComponent,
    KitsTemplateComponent,
    ApgAdministrationComponent,
    ProblemCategoryTemplateComponent,
    ProblemComponent,
    MdmResultProcessComponent,
    ResultEngineConfigComponent,
    ApgSampleResultViewComponent,
    ExteralSenderLoginTemplateComponent,
    ManufacturerConfigComponent,
    PlateViewComponent,
    TagsComponent,
    MdmTagSystemComponent,
    ArticLabPlanningComponent,
    MdmWorkflowComponent,
    ArticDeliveryPlanningComponent,
    MidplexMergeComponent,
    MidplexSamplesComponent,
    MdmSnpProfileComponent,
    EntryQcComponent,
    NgsVolumeAdjustmentComponent,
    NgsFragmentAnalysisComponent,
    NGSLibraryQCComponent,
    PrintPlateLabelsComponent,
    InvoicingItemComponent,
    CustomerTrackComponent,
    RunplanMainviewComponent,
    MdmFlowcellComponent,
    RunplanProcessComponent,
    MdmAnalysisComponent,
    MdmProcessComponent,
    MdmProcesstypeComponent,
    MdmStationComponent,
    MdmInboundFileProcessingComponent,
    RunplanOnholdprojectComponent,
    RunplanSchedulerComponent,
    MdmLibraryConfigurationComponent,
    MdmWorkfilesConfigComponent,
    RunplanTracklibrariesComponent,
    MdmStepCategoryComponent,
    LibprepWorkfilesComponent,
    NgsSampleArrivalComponent,
    ExtractionComponent
    ],
  imports: [
    NgbDropdownModule,
    BrowserModule,
    TableModule,
    TreeModule,
    TreeTableModule,
    TabViewModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TieredMenuModule,
    ButtonModule,
    InputSwitchModule,
    ToastModule,
    DialogModule,
    FormsModule,
    BlockUIModule,
    ProgressSpinnerModule,
    InputNumberModule,
    DropdownModule,
    FileUploadModule,
    FieldsetModule,
    InputTextModule,
    ListboxModule,
    AutoCompleteModule,
    CalendarModule,
    DataViewModule,
    ReactiveFormsModule,
    MessageModule,
    ChartModule,
    CheckboxModule,
    OverlayPanelModule,
    OrderListModule,
    TooltipModule,
    NgxChartsModule,
    ButtonModule,
    NgxFileHelpersModule,
    RadioButtonModule,
    SpinnerModule,
    ScrollPanelModule,
    PanelModule,
    MultiSelectModule,
    CardModule,
    ColorPickerModule,
    ConfirmDialogModule,
    SelectButtonModule,
    ButtonModule,
    GalleriaModule,
    PickListModule,
    ChipsModule,
    PaginatorModule,
    SidebarModule,
    SliderModule,
    ToggleButtonModule ,
    RouterModule.forRoot([
      { path: '', component: HeaderComponent, canActivate: [AzureAuthGuard, LabGuard] },
      { path: 'lab', component: LabComponent, canActivate: [AccessTokenGuard] },
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard]
      },
      {
        path: 'admin/masterdata',
        component: MasterdataComponent,
        canActivate: [AzureAuthGuard, LabGuard, MdmGuard]
      },
      {
        path: 'logout',
        component: LoginComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard]
      },
      {
        path: 'covid19entryNew',
        component: AgptestingNewviewComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        canDeactivate: [NewViewDirtyCheckGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'covid19unboxing',
        component: ApgtestingUnboxingComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        canDeactivate: [UnboxingDirtyCheckGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'apg-adminstration',
        component: ApgAdministrationComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'artic-lab-planning',
        component: ArticLabPlanningComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'artic-delivery-planning',
        component: ArticDeliveryPlanningComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'midplex-merge',
        component: MidplexMergeComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      { path: 'covid19entryNew/:id', component: AgptestingNewviewComponent },
      {
        path: 'covid19MidplexSamples',
        component: MidplexSamplesComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'entryQC',
        component: EntryQcComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'ngs-sample-arrival',
        component: NgsSampleArrivalComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'ngs-extraction',
        component: ExtractionComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'printCustomLabels',
        component: PrintPlateLabelsComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'invoicing-item',
        component: InvoicingItemComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'customer-track',
        component: CustomerTrackComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'runplan',
        component: RunplanMainviewComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'runprocess',
        component: RunplanProcessComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'runprocess/:id',
        component: RunplanProcessComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'measurements-results',
        canActivate: [AzureAuthGuard, LabGuard, BUGuard], data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] },
        loadChildren: () => import(  './measurements-view/measurements-view.module').then(m => m.MeasurementsImportViewModule)
      },
      {
        path: 'worklist',
        canActivate: [AzureAuthGuard, LabGuard, BUGuard], data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] },
        loadChildren: () => import(  './ngs-worklist-view.module').then(m => m.WorklistViewModule)
      },
      {
        path: 'runplan/onholdproject',
        component: RunplanOnholdprojectComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'runscheduling',
        component: RunplanSchedulerComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'workfile',
        component: MdmWorkfilesConfigComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'runplan/trackLibraies',
        component: RunplanTracklibrariesComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      },
      {
        path: 'libprep-workfiles',
        component: LibprepWorkfilesComponent,
        canActivate: [AzureAuthGuard, LabGuard, BUGuard],
        data: { claim: [environment.AZURE_NGS_KONSTANZ_GROUP_CLAIM, environment.AZURE_UK_DTC_POPGEN_LAB_USER_CLAIM] }
      }
    ]),
    KeyFilterModule,
    InputTextModule
  ],
  providers: [
    LabGuard,
    LabEmitterService,
    Globals,
    MessagesService,
    MessageService,
    TicketService,
    MasterDataService,
    { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    AuthGuard,
    AzureAuthGuard,
    MdmGuard,
    AccessTokenGuard,
    BUGuard,
    AuthenticationService,
    ConfirmationService,
    MdmProcessStepService,
    DatePipe,
    APGTestingService,
    AppService,
    UnboxingDirtyCheckGuard,
    NewViewDirtyCheckGuard,
    AzureAppInsightsService,
    RefreshAuthenticationService,
    APGAdministrationService,
    ProblemCategoryService,
    ProblemService,
    ResultProcessService,
    HeaderService,
    PrintService,
    NumberFormatPipe,
    ArticLabPlanningService,
    ArticDeliveryPlanningService,
    MidplexMergeService,
    MidplexSamplesService,
    NgsVolumeAdjustmentService,
    NgsFragmentAnalysisService,
    NGSLibraryQCService,
    PrintPlateLabelService,
    RunPlanMainViewService,
    RunplanProcessService,
    MdmInboundFileProcessingService,
    RunplanSchedulerService,
    MdmLibraryConfigurationService,
    MdmWorkfilesConfigService,
    RunplanTracklibrariesService,
    LibprepWorkfilesService,
    NgsSampleArrivalService,
    ExtractionService,
    PrintTemplateUtil
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
